import OpportunityCreditDocument from './OpportunityCreditDocument';
import OpportunityCreditMessageLogs from './OpportunityCreditMessageLogs';
import { useQuery } from '@tanstack/react-query';
import { OpportunityBackofficeDTO } from '../../../api/models/Opportunities/OpportunityBackofficeDTO';
import useSalesforceStore from '../../../store/SalesforceStore';
import { useApiClient } from '../../../hooks/useApiClient';
import _ from 'lodash';
import './opportunityDocumentAreas.scss';
import { Col, Container, Row } from 'react-bootstrap';
import LegacyOpportunityCreditDocument from './LegacyOpportunityCreditDocument';
import { useMemo } from 'react';
import { parseISO, startOfMonth, isBefore } from 'date-fns';
import { LEGACY_OPPORTUNITY_DATE } from 'src/utils/constants';
import { UserDTO } from 'src/api';
import { isMarket } from 'src/utils/markets';
import { Markets } from 'src/types';

type Props = {
  setShowVehicleNotAvailableModal?: any;
  isVehicleUnavailable?: boolean | null;
  isOpportunityReadOnly?: boolean | null;
  opportunitySalesUser?: UserDTO | null;
};

const OpportunityFileManagement = ({ setShowVehicleNotAvailableModal, isVehicleUnavailable, isOpportunityReadOnly, opportunitySalesUser }: Props) => {
  const { opportunityData } = useSalesforceStore();
  const { apiClient, handleApiResponse } = useApiClient();

  const { data: opportunityCreditData, isLoading: opportunityCreditDataLoading } = useQuery({
    queryKey: ['opportunityCreditData', opportunityData?.Id],
    queryFn: async (): Promise<OpportunityBackofficeDTO> => {
      let response = await apiClient.salesforceOpportunityController.getOpportunityBackoffice(opportunityData?.Id)

      const sortedResponse = {
        ...response,
        creditLogs: response?.logs
          ? _.orderBy(
            response.logs,
            [(log) => new Date(log.time ?? 0).getTime()],
            ['desc']
          )
          : [],
      };
      return sortedResponse;
    },
    enabled: !!opportunityData?.Id,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const isLegacy = useMemo(() => {
    if (isMarket(Markets.es)) return true;
    if (!opportunityData.Id) return false;
    const dateToCheck = parseISO(opportunityData.CreatedDate);
    const referenceDate = parseISO(LEGACY_OPPORTUNITY_DATE);
    return isBefore(dateToCheck, referenceDate);
  }, [opportunityCreditData, opportunityData])

  const creditDocumentComponent = isLegacy
    ? <LegacyOpportunityCreditDocument
      className="m-2 mr-0"
      data={opportunityCreditData}
      isVehicleUnavailable={isVehicleUnavailable}
      isOpportunityReadOnly={isOpportunityReadOnly}
      setShowVehicleNotAvailableModal={setShowVehicleNotAvailableModal}
      opportunityCreditDataLoading={opportunityCreditDataLoading}
    />
    : <OpportunityCreditDocument
      className="m-2 mr-0"
      data={opportunityCreditData}
      isVehicleUnavailable={isVehicleUnavailable}
      isOpportunityReadOnly={isOpportunityReadOnly}
      setShowVehicleNotAvailableModal={setShowVehicleNotAvailableModal}
      opportunityCreditDataLoading={opportunityCreditDataLoading}
      opportunitySalesUser={opportunitySalesUser}
    />;

  const isLoading = !opportunityData?.Id || !opportunityCreditData;

  return (
    <Container fluid style={{ minHeight: '100vh' }} className="p-0 m-0 card mt-4 p-2">
      <Row className="m-0">
        <Col lg={6} md={12} className="p-0 m-0">
          {!isLoading && creditDocumentComponent}
        </Col>
        <Col lg={6} md={12} className="p-0 m-0 mb-4">
          <fieldset disabled={!!isOpportunityReadOnly}>
            <OpportunityCreditMessageLogs
              className="m-2 mb-4 ps-0"
              isLoading={opportunityCreditDataLoading}
              logs={opportunityCreditData?.logs || []}
            />
          </fieldset>
        </Col>
      </Row>
    </Container>
  );
}

export default OpportunityFileManagement
